export class SoundManager {
  private ctx: AudioContext | null = null;
  private engineOscillator: OscillatorNode | null = null;
  private engineLfo: OscillatorNode | null = null;
  private engineGain: GainNode | null = null;
  private engineFilter: BiquadFilterNode | null = null;
  private masterGain: GainNode | null = null;
  private sfxGain: GainNode | null = null;  // Separate channel for sound effects
  private isEngineRunning = false;

  init() {
    if (this.ctx) return;
    this.ctx = new AudioContext();

    // Master gain for overall volume
    this.masterGain = this.ctx.createGain();
    this.masterGain.gain.value = 0.3;
    this.masterGain.connect(this.ctx.destination);

    // Separate gain for sound effects (doesn't affect engine)
    this.sfxGain = this.ctx.createGain();
    this.sfxGain.gain.value = 0.3;
    this.sfxGain.connect(this.ctx.destination);
  }

  private ensureContext() {
    if (!this.ctx) this.init();
    if (this.ctx?.state === 'suspended') {
      this.ctx.resume();
    }
  }

  startEngine() {
    this.ensureContext();
    if (!this.ctx || this.isEngineRunning) return;

    // Create engine gain - connects to master (separate from sfx)
    this.engineGain = this.ctx.createGain();
    this.engineGain.gain.value = 0;
    this.engineGain.connect(this.masterGain!);

    // Main engine tone
    this.engineOscillator = this.ctx.createOscillator();
    this.engineOscillator.type = 'sawtooth';
    this.engineOscillator.frequency.value = 80;

    // Add some variation (store LFO to prevent garbage collection)
    this.engineLfo = this.ctx.createOscillator();
    this.engineLfo.frequency.value = 6;
    const lfoGain = this.ctx.createGain();
    lfoGain.gain.value = 5;
    this.engineLfo.connect(lfoGain);
    lfoGain.connect(this.engineOscillator.frequency);

    // Filter for more realistic sound (store to prevent GC)
    this.engineFilter = this.ctx.createBiquadFilter();
    this.engineFilter.type = 'lowpass';
    this.engineFilter.frequency.value = 300;

    // Connect: oscillator -> filter -> gain -> master
    this.engineOscillator.connect(this.engineFilter);
    this.engineFilter.connect(this.engineGain);

    // Start oscillators
    this.engineOscillator.start();
    this.engineLfo.start();

    // Fade in
    this.engineGain.gain.setTargetAtTime(0.4, this.ctx.currentTime, 0.3);
    this.isEngineRunning = true;
  }

  stopEngine() {
    if (!this.engineGain || !this.ctx) return;
    if (!this.isEngineRunning) return;

    // Fade out
    this.engineGain.gain.setTargetAtTime(0, this.ctx.currentTime, 0.2);

    // Stop after fade
    setTimeout(() => {
      try {
        this.engineOscillator?.stop();
        this.engineLfo?.stop();
      } catch (_) { /* ignore if already stopped */ }
      this.engineOscillator = null;
      this.engineLfo = null;
      this.engineFilter = null;
      this.engineGain = null;
      this.isEngineRunning = false;
    }, 500);
  }

  playCollect() {
    this.ensureContext();
    if (!this.ctx) return;

    const now = this.ctx.currentTime;

    // Coin sound - two quick high-pitched tones
    const osc1 = this.ctx.createOscillator();
    const osc2 = this.ctx.createOscillator();
    const gain = this.ctx.createGain();

    osc1.type = 'sine';
    osc2.type = 'sine';
    osc1.frequency.value = 1200;
    osc2.frequency.value = 1600;

    gain.gain.value = 0.2;
    gain.gain.setTargetAtTime(0, now + 0.1, 0.05);

    osc1.connect(gain);
    osc2.connect(gain);
    gain.connect(this.masterGain!);

    osc1.start(now);
    osc2.start(now + 0.05);
    osc1.stop(now + 0.15);
    osc2.stop(now + 0.2);
  }

  playMultiplier() {
    this.ensureContext();
    if (!this.ctx) return;

    const now = this.ctx.currentTime;

    // Rising tone for multiplier
    const osc = this.ctx.createOscillator();
    const gain = this.ctx.createGain();

    osc.type = 'sine';
    osc.frequency.value = 400;
    osc.frequency.exponentialRampToValueAtTime(1200, now + 0.3);

    gain.gain.value = 0.2;
    gain.gain.setTargetAtTime(0, now + 0.2, 0.1);

    osc.connect(gain);
    gain.connect(this.masterGain!);

    osc.start(now);
    osc.stop(now + 0.4);
  }

  // Player crash explosion - stops engine
  playExplosion() {
    this.playExplosionSound();
    this.stopEngine();
  }

  // Rocket/enemy explosion - doesn't affect engine
  playRocketExplosion() {
    this.playExplosionSound();
  }

  private playExplosionSound() {
    this.ensureContext();
    if (!this.ctx || !this.sfxGain) return;

    const now = this.ctx.currentTime;

    // White noise for explosion - use sfxGain
    const bufferSize = this.ctx.sampleRate * 0.5;
    const buffer = this.ctx.createBuffer(1, bufferSize, this.ctx.sampleRate);
    const data = buffer.getChannelData(0);

    for (let i = 0; i < bufferSize; i++) {
      data[i] = Math.random() * 2 - 1;
    }

    const noise = this.ctx.createBufferSource();
    noise.buffer = buffer;

    // Filter for rumble
    const filter = this.ctx.createBiquadFilter();
    filter.type = 'lowpass';
    filter.frequency.value = 1000;
    filter.frequency.exponentialRampToValueAtTime(100, now + 0.4);

    const gain = this.ctx.createGain();
    gain.gain.value = 0.4;
    gain.gain.exponentialRampToValueAtTime(0.01, now + 0.5);

    noise.connect(filter);
    filter.connect(gain);
    gain.connect(this.sfxGain);

    noise.start(now);
    noise.stop(now + 0.5);

    // Add a low thump
    const thump = this.ctx.createOscillator();
    const thumpGain = this.ctx.createGain();
    thump.type = 'sine';
    thump.frequency.value = 60;
    thump.frequency.exponentialRampToValueAtTime(20, now + 0.3);
    thumpGain.gain.value = 0.5;
    thumpGain.gain.exponentialRampToValueAtTime(0.01, now + 0.3);

    thump.connect(thumpGain);
    thumpGain.connect(this.sfxGain);
    thump.start(now);
    thump.stop(now + 0.3);
  }

  playShoot() {
    this.ensureContext();
    if (!this.ctx || !this.sfxGain) return;

    const now = this.ctx.currentTime;

    // Short, sharp gunshot sound - use sfxGain to not affect engine
    const noise = this.createNoise(0.08);
    const filter = this.ctx.createBiquadFilter();
    filter.type = 'highpass';
    filter.frequency.value = 800;

    const gain = this.ctx.createGain();
    gain.gain.value = 0.3;
    gain.gain.exponentialRampToValueAtTime(0.01, now + 0.08);

    noise.connect(filter);
    filter.connect(gain);
    gain.connect(this.sfxGain);

    noise.start(now);
    noise.stop(now + 0.08);

    // Add a quick click
    const click = this.ctx.createOscillator();
    const clickGain = this.ctx.createGain();
    click.type = 'square';
    click.frequency.value = 150;
    clickGain.gain.value = 0.2;
    clickGain.gain.exponentialRampToValueAtTime(0.01, now + 0.03);

    click.connect(clickGain);
    clickGain.connect(this.sfxGain);
    click.start(now);
    click.stop(now + 0.03);
  }

  playHit() {
    this.ensureContext();
    if (!this.ctx || !this.sfxGain) return;

    const now = this.ctx.currentTime;

    // Impact sound - metallic ping - use sfxGain
    const osc = this.ctx.createOscillator();
    const gain = this.ctx.createGain();

    osc.type = 'triangle';
    osc.frequency.value = 800;
    osc.frequency.exponentialRampToValueAtTime(200, now + 0.15);

    gain.gain.value = 0.25;
    gain.gain.exponentialRampToValueAtTime(0.01, now + 0.15);

    osc.connect(gain);
    gain.connect(this.sfxGain);

    osc.start(now);
    osc.stop(now + 0.15);

    // Add metallic noise
    const noise = this.createNoise(0.1);
    const filter = this.ctx.createBiquadFilter();
    filter.type = 'bandpass';
    filter.frequency.value = 2000;
    filter.Q.value = 5;

    const noiseGain = this.ctx.createGain();
    noiseGain.gain.value = 0.15;
    noiseGain.gain.exponentialRampToValueAtTime(0.01, now + 0.1);

    noise.connect(filter);
    filter.connect(noiseGain);
    noiseGain.connect(this.sfxGain);

    noise.start(now);
    noise.stop(now + 0.1);
  }

  playCashRegister() {
    this.ensureContext();
    if (!this.ctx || !this.sfxGain) return;

    const now = this.ctx.currentTime;

    // "Cha-ching" - two quick high bells
    const bell1 = this.ctx.createOscillator();
    const bell2 = this.ctx.createOscillator();
    const bellGain = this.ctx.createGain();

    bell1.type = 'sine';
    bell2.type = 'sine';
    bell1.frequency.value = 1800;
    bell2.frequency.value = 2400;

    bellGain.gain.value = 0.25;
    bellGain.gain.setTargetAtTime(0, now + 0.08, 0.05);

    bell1.connect(bellGain);
    bell2.connect(bellGain);
    bellGain.connect(this.sfxGain);

    bell1.start(now);
    bell2.start(now + 0.08);
    bell1.stop(now + 0.15);
    bell2.stop(now + 0.25);

    // Mechanical click
    const click = this.ctx.createOscillator();
    const clickGain = this.ctx.createGain();
    click.type = 'square';
    click.frequency.value = 100;
    clickGain.gain.value = 0.15;
    clickGain.gain.exponentialRampToValueAtTime(0.01, now + 0.02);

    click.connect(clickGain);
    clickGain.connect(this.sfxGain);
    click.start(now);
    click.stop(now + 0.02);

    // Final ring
    const ring = this.ctx.createOscillator();
    const ringGain = this.ctx.createGain();
    ring.type = 'sine';
    ring.frequency.value = 3000;
    ringGain.gain.value = 0.2;
    ringGain.gain.exponentialRampToValueAtTime(0.01, now + 0.4);

    ring.connect(ringGain);
    ringGain.connect(this.sfxGain);
    ring.start(now + 0.15);
    ring.stop(now + 0.4);
  }

  private createNoise(duration: number): AudioBufferSourceNode {
    const bufferSize = Math.ceil(this.ctx!.sampleRate * duration);
    const buffer = this.ctx!.createBuffer(1, bufferSize, this.ctx!.sampleRate);
    const data = buffer.getChannelData(0);

    for (let i = 0; i < bufferSize; i++) {
      data[i] = Math.random() * 2 - 1;
    }

    const noise = this.ctx!.createBufferSource();
    noise.buffer = buffer;
    return noise;
  }

  setVolume(value: number) {
    if (this.masterGain) {
      this.masterGain.gain.value = Math.max(0, Math.min(1, value));
    }
  }

  setMuted(muted: boolean) {
    if (this.masterGain) {
      this.masterGain.gain.value = muted ? 0 : 0.3;
    }
  }
}

export const soundManager = new SoundManager();
